import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	TouchableOpacity,
	StyleSheet,
	View,
	Image,
	Animated,
	Easing,
	LayoutAnimation,
	ActionSheetIOS,
	Platform,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import errorHandler from 'src/core/utils/errorHandler';
import Translations from 'src/core/utils/Translations';
import AlertHelper from 'src/presentation/helpers/AlertHelper';
import EventLink from 'src/presentation/components/molecules/EventLink';
import { useEditModeStore } from 'src/domain/stores/useEditModeStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useAnalyticsStore } from 'src/domain/stores/useAnalyticsStore';
import Icons from 'src/core/utils/Icons';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import type { ApiEvent } from 'src/core/models/api/ApiEvent';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';

const isIos = Platform.OS === 'ios';

const iosAnimationConfig = {
	duration: 280,
	create: { type: 'easeInEaseOut', property: 'opacity' },
	update: { type: 'easeInEaseOut', springDamping: 0.6, property: 'scaleXY' },
	delete: { type: 'easeInEaseOut', property: 'opacity' },
};

function EventGrid() {
	const editMode = useEditModeStore();
	const eventStore = useEventStore();
	const authStore = useAuthenticationStore();
	const navigationStore = useNavigationStore();
	const analyticsStore = useAnalyticsStore();

	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const scaleValue = useRef(new Animated.Value(0)).current;
	const prevEventCountRef = useRef(eventStore.eventData.length);
	const prevSelectedCountRef = useRef(eventStore.selectedEvents.length);
	const prevEditDisabledRef = useRef(editMode.disabled);
	const [loadingUuid, setLoadingUuid] = useState<string | null>(null);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		analyticsStore.eventTime(eventStore.activeEventUuid, 'end');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function scaleUp(delay?: number) {
		Animated.timing(scaleValue, {
			toValue: 1,
			duration: 210,
			easing: Easing.elastic(1.6),
			useNativeDriver: true,
			delay,
		}).start();
	}

	function scaleDown(delay?: number) {
		Animated.timing(scaleValue, {
			toValue: 0,
			duration: 120,
			easing: Easing.elastic(1),
			useNativeDriver: true,
			delay,
		}).start();
	}

	const onPressCell = async (data: ApiEvent) => {
		// Online-first: viewing an event requires login, mirroring the add-event gate.
		if (!authStore.isLoggedin) {
			AlertHelper.showConfirmAlert({
				title: Translations.alertLoginRequiredTitle,
				message: Translations.alertLoginRequiredViewMessage,
				confirmText: Translations.buttonLogin,
				cancelText: Translations.buttonCancel,
				onConfirm: () => {
					eventStore.setPendingViewEventUuid(data.uuid);
					navigation.navigate(Constants.screenLogin);
				},
			});
			return;
		}

		if (isIos && data.theme?.data) {
			eventStore.setTheme(data.theme.data);
		}

		analyticsStore.eventOpened(data.uuid);
		analyticsStore.eventTime(data.uuid, 'start');
		setLoadingUuid(data.uuid);
		try {
			await eventStore.fetchEvent(data.uuid);
		} finally {
			setLoadingUuid(null);
		}

		const updatedEvent = eventStore.eventData.find((e) => e.uuid === data.uuid) ?? data;

		if (!isIos) {
			navigationStore.setActiveEventByCode(updatedEvent.code);
		}
		navigationStore.setActiveEventByUuid(updatedEvent.uuid);

		if (!isIos) {
			navigationStore.navigate(Constants.screenEvent, {
				title: updatedEvent.name,
				eventLogo: updatedEvent.logo,
				eventUuid: updatedEvent.uuid,
			});
		}
		navigation.navigate(Constants.screenEvent, {
			title: updatedEvent.name,
			eventLogo: updatedEvent.logo,
			eventUuid: updatedEvent.uuid,
		});
	};

	// After a login triggered by tapping an event while logged out: reopen that event on return to Home.
	useFocusEffect(
		useCallback(() => {
			const { pendingViewEventUuid, setPendingViewEventUuid, eventData } = useEventStore.getState();
			if (!pendingViewEventUuid) return;

			if (useAuthenticationStore.getState().isLoggedin) {
				setPendingViewEventUuid(null);
				const event = eventData.find((e) => e.uuid === pendingViewEventUuid);
				if (event) requestAnimationFrame(() => onPressCell(event).catch(() => setLoadingUuid(null)));
			} else {
				// Login cancelled -> drop the intent.
				setPendingViewEventUuid(null);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []),
	);

	const onLongPressCell = () => {
		editMode.toggle();
	};

	const onRemoveCellButtonPress = (data: ApiEvent) => {
		const confirmDelete = () => {
			if (navigationStore.isConnected) {
				eventStore.deleteEvent(data.code, data.uuid);
			} else {
				errorHandler(Translations.errorDeleteEventOffline, null);
			}
		};
		const title = data.name + ' ' + Translations.buttonDelete.toLowerCase() + '?';

		if (isIos) {
			ActionSheetIOS.showActionSheetWithOptions(
				{
					options: [Translations.buttonDelete, Translations.buttonCancel],
					cancelButtonIndex: 1,
					destructiveButtonIndex: 0,
					title,
				},
				(buttonIndex) => {
					if (buttonIndex === 0) confirmDelete();
				},
			);
			return;
		}

		// Android: same per-event delete with a confirmation dialog as iOS.
		AlertHelper.showConfirmAlert({
			title,
			confirmText: Translations.buttonDelete,
			confirmButtonStyle: 'destructive',
			onConfirm: confirmDelete,
		});
	};

	const renderEventLinks = (events: ApiEvent[]) =>
		events.map((data, index) => {
			const isCellLoading = loadingUuid === data.uuid;
			return (
				// Keyed by uuid on both platforms: two events can share a name, and a duplicate key makes React reuse the
				// wrong cell (the loading state landed on another tile) besides logging a warning.
				<View
					key={data.uuid}
					style={index % 2 === 0 ? [styles.event, styles.eventLeft] : [styles.event, styles.eventRight]}
				>
					<TouchableOpacity
						disabled={editMode.isEditing || isCellLoading}
						key={data.uuid}
						onLongPress={isIos ? undefined : () => onLongPressCell()}
						onPress={() => {
							onPressCell(data).catch(() => setLoadingUuid(null));
						}}
					>
						<EventLink
							image={data.image}
							isLoading={isCellLoading}
							theme={data.theme?.data}
							title={data.name}
							uuid={data.uuid}
						/>
						{isIos ? (
							<TouchableOpacity
								activeOpacity={0.8}
								disabled={!editMode.disabled}
								style={styles.removeBtn}
								onPress={() => onRemoveCellButtonPress(data)}
							>
								{(() => {
									editMode.disabled ? scaleUp() : scaleDown();
									return null;
								})()}
								<Animated.Image source={Icons.deleteIos} style={[styles.iconIos, { transform: [{ scale: scaleValue }] }]} />
							</TouchableOpacity>
						) : (
							<TouchableOpacity
								activeOpacity={0.8}
								disabled={!editMode.isEditing}
								style={[styles.removeBtn, { opacity: editMode.isEditing ? 1 : 0 }]}
								onPress={() => onRemoveCellButtonPress(data)}
							>
								<Image source={Icons.deleteIos} style={styles.iconIos} />
							</TouchableOpacity>
						)}
					</TouchableOpacity>
				</View>
			);
		});

	const events = eventStore.eventData;

	if (!events.length) return null;

	if (isIos) {
		const eventCountChanged = prevEventCountRef.current !== eventStore.eventData.length;
		const selectedCountChanged = prevSelectedCountRef.current !== eventStore.selectedEvents.length;
		const editModeChanged = prevEditDisabledRef.current !== editMode.disabled;
		if (eventCountChanged || selectedCountChanged || editModeChanged) {
			LayoutAnimation.configureNext(iosAnimationConfig as Parameters<typeof LayoutAnimation.configureNext>[0]);
			prevEventCountRef.current = eventStore.eventData.length;
			prevSelectedCountRef.current = eventStore.selectedEvents.length;
			prevEditDisabledRef.current = editMode.disabled;
		}
	}

	return <View style={styles.container}>{renderEventLinks(events)}</View>;
}

export default EventGrid;

const styles = StyleSheet.create({
	container: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		paddingLeft: isIos ? Dimens.padding20 : Dimens.padding16,
		paddingRight: isIos ? Dimens.padding20 : Dimens.padding16,
		paddingTop: Dimens.padding24,
	},
	iconIos: {
		position: 'absolute',
		right: -12,
		zIndex: 1,
		top: -12,
		backgroundColor: Colors.transparent,
	},
	// Half of whatever the row actually is, rather than half of the column minus its padding. The
	// two are not the same: inside a vertical ScrollView a scrollbar takes about 14px, so the
	// arithmetic version asked for 468px of a row that was 454px wide and the second tile wrapped
	// onto its own line -- which is how "the tiles are underneath each other" came in. A percentage
	// resolves against the real parent, so it cannot drift from it. The 8px below sits inside the
	// half, so two halves still add up to the row.
	event: isIos
		? {
				width: '50%',
				justifyContent: 'center',
			}
		: {
				width: '50%',
				justifyContent: 'center',
				position: 'relative',
				marginBottom: Dimens.margin5,
			},
	eventRight: {
		paddingLeft: isIos ? Dimens.padding10 : Dimens.padding8,
	},
	eventLeft: {
		paddingRight: isIos ? Dimens.padding10 : Dimens.padding8,
	},
	removeBtn: {
		position: 'absolute',
		right: 0,
		top: 0,
		width: 30,
		height: 30,
	},
});
